/**
 * Voice and alert sounds, synthesised at run time with the Web Audio API.
 *
 * Every sound is our own: a few soft sine and triangle partials with a
 * gentle attack and an exponential fade (Pearl Glass: calm, rounded, never
 * harsh). Nothing is fetched and no audio file is bundled. Discord's and
 * TeamSpeak's sounds are not copied or imitated note for note.
 *
 * Settings > Sounds: master on/off, volume, and on/off per kind. While you
 * are deafened only the mute and deafen cues play (you asked for silence).
 */
import { create } from 'zustand';
import { storage } from '../lib/storage';

export type SoundEvent =
  | 'join'
  | 'leave'
  | 'move'
  | 'peerJoin'
  | 'peerLeave'
  | 'mute'
  | 'unmute'
  | 'deafen'
  | 'undeafen'
  | 'pttOn'
  | 'pttOff'
  | 'disconnected'
  | 'reconnected'
  | 'poke'
  | 'message'
  /** A DM call ringing you, and the ringback while you ring others (2026-11-01). */
  | 'ring'
  | 'ringback'
  /** A stream starting or ending in your room, a viewer joining or leaving yours (2026-11-02). */
  | 'streamStart'
  | 'streamEnd'
  | 'viewerJoin'
  | 'viewerLeave';

/** What Settings > Sounds turns on and off (one switch may cover a pair). */
export type SoundKind =
  | 'join'
  | 'leave'
  | 'move'
  | 'peerJoin'
  | 'peerLeave'
  | 'mute'
  | 'deafen'
  | 'ptt'
  | 'connection'
  | 'poke'
  | 'message'
  | 'call'
  | 'streamStart'
  | 'streamEnd'
  | 'viewerJoin'
  | 'viewerLeave';

/**
 * The switches Settings > Sounds lists. A poke has its own buzz, switch and
 * volume (Settings > Notifications, pokes/alert.ts), so it is not listed.
 */
export const SOUND_KINDS: SoundKind[] = [
  'join',
  'leave',
  'move',
  'peerJoin',
  'peerLeave',
  'mute',
  'deafen',
  'ptt',
  'connection',
  'message',
  'call',
  'streamStart',
  'streamEnd',
  'viewerJoin',
  'viewerLeave',
];

export const KIND_OF: Record<SoundEvent, SoundKind> = {
  join: 'join',
  leave: 'leave',
  move: 'move',
  peerJoin: 'peerJoin',
  peerLeave: 'peerLeave',
  mute: 'mute',
  unmute: 'mute',
  deafen: 'deafen',
  undeafen: 'deafen',
  pttOn: 'ptt',
  pttOff: 'ptt',
  disconnected: 'connection',
  reconnected: 'connection',
  poke: 'poke',
  message: 'message',
  ring: 'call',
  ringback: 'call',
  streamStart: 'streamStart',
  streamEnd: 'streamEnd',
  viewerJoin: 'viewerJoin',
  viewerLeave: 'viewerLeave',
};

/** One partial: frequency (Hz), start and length (s), peak gain, optional glide. */
interface Tone {
  f: number;
  t: number;
  d: number;
  g: number;
  to?: number;
  w?: OscillatorType;
}

// Notes (Hz), for reading the table below.
const D4 = 293.66;
const F4 = 349.23;
const G4 = 392;
const A4 = 440;
const B4 = 493.88;
const D5 = 587.33;
const E5 = 659.25;
const Fs5 = 739.99;
const G5 = 783.99;
const A5 = 880;
const B5 = 987.77;
const C6 = 1046.5;
const D6 = 1174.66;
const E6 = 1318.51;
const G6 = 1567.98;

/** A soft bell: the note plus a quiet octave partial. */
const bell = (f: number, t: number, d: number, g: number): Tone[] => [
  { f, t, d, g },
  { f: f * 2, t, d: d * 0.6, g: g * 0.18 },
];

/** Each event's sound: short (at most ~0.7 s), distinct in contour and pitch. */
export const SOUNDS: Record<SoundEvent, Tone[]> = {
  // you: rising for in, falling for out, a quick step for a move
  join: [...bell(E5, 0, 0.22, 0.32), ...bell(A5, 0.09, 0.32, 0.3)],
  leave: [...bell(A5, 0, 0.2, 0.28), ...bell(E5, 0.09, 0.34, 0.28)],
  move: [...bell(B5, 0, 0.12, 0.22), ...bell(E6, 0.06, 0.2, 0.2)],
  // others in your channel: softer, single gliding tones
  peerJoin: [{ f: G5, to: C6, t: 0, d: 0.24, g: 0.2, w: 'triangle' }],
  peerLeave: [{ f: C6, to: G5, t: 0, d: 0.26, g: 0.18, w: 'triangle' }],
  // mic: a low rounded tap down for mute, up for unmute
  mute: [{ f: A4, to: F4, t: 0, d: 0.12, g: 0.3, w: 'triangle' }],
  unmute: [{ f: F4, to: A4, t: 0, d: 0.12, g: 0.3, w: 'triangle' }],
  // headphones: two low steps
  deafen: [...bell(G4, 0, 0.14, 0.28), ...bell(D4, 0.08, 0.22, 0.28)],
  undeafen: [...bell(D4, 0, 0.14, 0.28), ...bell(G4, 0.08, 0.22, 0.28)],
  // push-to-talk: barely there
  pttOn: [{ f: D6, t: 0, d: 0.045, g: 0.07 }],
  pttOff: [{ f: A5, t: 0, d: 0.045, g: 0.055 }],
  // the connection: a minor fall when lost, a major rise when back
  disconnected: [
    ...bell(A4, 0, 0.2, 0.26),
    ...bell(F4, 0.12, 0.2, 0.26),
    ...bell(D4, 0.24, 0.36, 0.26),
  ],
  reconnected: [
    ...bell(D5, 0, 0.16, 0.24),
    ...bell(Fs5, 0.08, 0.16, 0.24),
    ...bell(A5, 0.16, 0.3, 0.24),
  ],
  // attention: brighter, still round
  poke: [...bell(C6, 0, 0.12, 0.26), ...bell(G6, 0.1, 0.12, 0.2), ...bell(C6, 0.2, 0.24, 0.22)],
  message: [...bell(G5, 0, 0.18, 0.22), ...bell(D6, 0.1, 0.34, 0.2)],
  // a DM call (2026-11-01; repeated while it rings): a bright double chime,
  // twice; the ringback you hear while ringing others, one soft low pair
  ring: [
    ...bell(E5, 0, 0.26, 0.28),
    ...bell(B5, 0.12, 0.3, 0.26),
    ...bell(E5, 0.52, 0.26, 0.28),
    ...bell(B5, 0.64, 0.36, 0.26),
  ],
  ringback: [...bell(D5, 0, 0.42, 0.14), ...bell(Fs5, 0.2, 0.46, 0.12)],
  // streams in your room (2026-11-02): a bright three-step rise when one
  // goes live, the same steps down when it ends; a viewer of yours, a
  // quiet pair up as they start watching and down as they stop
  streamStart: [
    ...bell(G5, 0, 0.12, 0.22),
    ...bell(B5, 0.07, 0.12, 0.2),
    ...bell(E6, 0.14, 0.26, 0.2),
  ],
  streamEnd: [
    ...bell(E6, 0, 0.12, 0.2),
    ...bell(B5, 0.07, 0.12, 0.2),
    ...bell(G5, 0.14, 0.28, 0.22),
  ],
  viewerJoin: [...bell(A5, 0, 0.1, 0.16), ...bell(E6, 0.07, 0.18, 0.14)],
  viewerLeave: [...bell(E6, 0, 0.1, 0.14), ...bell(A5, 0.07, 0.2, 0.16)],
};
// keep B4 referenced for future cues without a lint warning
void B4;

export interface SoundPrefs {
  enabled: boolean;
  /** 0..1 */
  volume: number;
  /** Kinds turned off. */
  off: Partial<Record<SoundKind, boolean>>;
}

const DEFAULTS: SoundPrefs = { enabled: true, volume: 0.6, off: {} };

interface SoundStore {
  prefs: SoundPrefs;
  setPrefs(p: Partial<SoundPrefs>): void;
  setKind(k: SoundKind, on: boolean): void;
}

export const useSounds = create<SoundStore>((set) => ({
  prefs: { ...DEFAULTS, ...storage.getJson<Partial<SoundPrefs>>('sound-prefs', {}) },
  setPrefs(p) {
    set((st) => {
      const prefs = { ...st.prefs, ...p };
      storage.setJson('sound-prefs', prefs);
      return { prefs };
    });
  },
  setKind(k, on) {
    set((st) => {
      const prefs = { ...st.prefs, off: { ...st.prefs.off, [k]: !on } };
      storage.setJson('sound-prefs', prefs);
      return { prefs };
    });
  },
}));

/** Sounds that still play while deafened. */
const WHILE_DEAF: ReadonlySet<SoundEvent> = new Set(['mute', 'unmute', 'deafen', 'undeafen']);

let makeContext: () => AudioContext | null = () => {
  const C =
    typeof window === 'undefined'
      ? undefined
      : (window.AudioContext ??
        (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
  return C ? new C() : null;
};
let ctx: AudioContext | null = null;
let deafened: () => boolean = () => false;
let listener: ((ev: SoundEvent) => void) | null = null;

/** Tests swap the AudioContext. */
export function setAudioContextFactory(f: () => AudioContext | null): void {
  makeContext = f;
  ctx = null;
}

/** The one AudioContext the app's sounds share (made on first use), or null where there is none. */
export function audioContext(): AudioContext | null {
  ctx ??= makeContext();
  return ctx;
}

/** The voice wiring tells the player whether you are deafened. */
export function setDeafenedProbe(f: () => boolean): void {
  deafened = f;
}

/** Tests observe what played. */
export function onSoundPlayed(f: ((ev: SoundEvent) => void) | null): void {
  listener = f;
}

/** Would `ev` play now (settings and deafen rule)? */
export function shouldPlay(ev: SoundEvent, prefs = useSounds.getState().prefs): boolean {
  if (!prefs.enabled || prefs.volume <= 0) return false;
  if (prefs.off[KIND_OF[ev]]) return false;
  if (deafened() && !WHILE_DEAF.has(ev)) return false;
  return true;
}

/** Plays one sound if the settings allow it. `preview` ignores the switches (Settings' play buttons). */
export function play(ev: SoundEvent, o: { preview?: boolean } = {}): boolean {
  const prefs = useSounds.getState().prefs;
  if (!o.preview && !shouldPlay(ev, prefs)) return false;
  const volume = o.preview && prefs.volume <= 0 ? 0.6 : prefs.volume;
  try {
    ctx ??= makeContext();
    if (!ctx) return false;
    if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
    synth(ctx, SOUNDS[ev], volume);
  } catch {
    return false;
  }
  listener?.(ev);
  return true;
}

function synth(c: AudioContext, tones: Tone[], volume: number): void {
  const t0 = c.currentTime + 0.01;
  const master = c.createGain();
  // a gentle curve: half volume sounds half as loud
  master.gain.value = Math.pow(Math.max(0, Math.min(1, volume)), 1.6) * 0.9;
  master.connect(c.destination);
  let end = t0;
  for (const tone of tones) {
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = tone.w ?? 'sine';
    const s = t0 + tone.t;
    const e = s + tone.d;
    o.frequency.setValueAtTime(tone.f, s);
    if (tone.to) o.frequency.exponentialRampToValueAtTime(tone.to, e);
    g.gain.setValueAtTime(0.0001, s);
    g.gain.exponentialRampToValueAtTime(tone.g, s + Math.min(0.012, tone.d / 4));
    g.gain.exponentialRampToValueAtTime(0.0001, e);
    o.connect(g).connect(master);
    o.start(s);
    o.stop(e + 0.02);
    end = Math.max(end, e);
  }
  setTimeout(
    () => {
      try {
        master.disconnect();
      } catch {
        /* already gone */
      }
    },
    (end - t0 + 0.2) * 1000,
  );
}
